// frontend/app/components/lessonPlans/present/PresentAssetBlock.js
//
// The 3D scene, the video segment and the topic quiz — the things a projector
// is actually for. In the workspace these are a 14px card in a column of cards,
// which is the correct weight when a student is reading around them and the
// wrong weight entirely when the scene IS the lesson for the next ten minutes.
// Here the asset gets a stage: full stage width, sized off the viewport height,
// and nothing else on the slide competing with it.
//
// The scene and the video play HERE, unlike everywhere else in the app.
// Handing the launch back to the caller is what the workspace does, and it
// cannot work in this mode: the workspace owns LessonPwoPlayer and
// LessonVideoPlayer and the workspace is not mounted. A projector showing a
// launch button with no player behind it is a slide that does nothing.
//
// The topic quiz is the exception and still hands back, because its player
// records answers and present mode must never write one.
//
// Opening is not recording. These players take a token and asset ids and give
// back pixels; this component has no answer to store.

import React, { useCallback, useState } from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import LessonPwoPlayer from "../LessonPwoPlayer";
import LessonVideoPlayer from "../LessonVideoPlayer";
import { ChromeButton, PresentBody } from "./PresentSurface";
import { assetSummaryFor } from "../assetSummary";
import { ASSET_TIER_EXTENDED, assetTierOf } from "../lessonPlanRules";

/** Kind, icon and accent per asset type. Accents match the workspace's cards. */
const ASSET_KINDS = {
  saras_pwo: {
    icon: "cube-outline",
    accent: "#10b981",
    kind: "3D Scene",
    action: "Open 3D scene",
  },
  saras_video: {
    icon: "play-circle-outline",
    accent: "#3b82f6",
    kind: "Video segment",
    action: "Play segment",
  },
  saras_topic_quiz: {
    icon: "clipboard-check-outline",
    accent: "#8b5cf6",
    kind: "Topic Quiz",
    action: "Open quiz",
  },
};

const formatStartTime = (resolved) => {
  if (!resolved) return null;
  if (resolved.tag_start_time) return resolved.tag_start_time;
  const seconds = Number(resolved.start_seconds || 0);
  if (!seconds) return null;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
};

/**
 * Names shown come from the backend's resolved payload, never from strings
 * authored into the lesson — the same rule the workspace's asset cards follow,
 * and what lets one lesson survive content being re-cut or re-localized.
 */
const metaFor = (block, translate) => {
  const resolved = block.resolved_asset || {};
  const meta = [];

  if (block.type === "saras_pwo") {
    if (resolved.pwo_name) meta.push(resolved.pwo_name);
    if (resolved.scene_display_name) meta.push(resolved.scene_display_name);
  }
  if (block.type === "saras_video") {
    if (resolved.video_name) meta.push(resolved.video_name);
    const startTime = formatStartTime(resolved);
    if (startTime) meta.push(`${translate("Starts at")} ${startTime}`);
  }
  if (block.type === "saras_topic_quiz" && resolved.question_count) {
    meta.push(`${resolved.question_count} ${translate("questions")}`);
  }

  return meta;
};

const titleFor = (block, translate) => {
  const resolved = block.resolved_asset || {};
  if (block.title) return block.title;
  if (block.type === "saras_pwo") {
    return resolved.scene_display_name || translate("3D Model");
  }
  if (block.type === "saras_video") {
    return resolved.tag_name || translate("Concept video");
  }
  return translate("Topic Quiz");
};

const PresentAssetBlock = ({
  block,
  colors,
  isDarkMode = false,
  type,
  authToken,
  localLanguage = "en",
  languageId = 1,
  onOpenAsset,
  // Opens the Saras panel already asking this. Present mode's own, and the same
  // one an ai_prompt question on a slide uses.
  onAskQuestion,
  // Opens the Saras panel showing authored text, with no ask behind it. Only
  // the host that owns the panel can do that, so its absence is meaningful: see
  // the summary control below for what happens without it.
  onShowSummary,
  t,
}) => {
  const translate = t || ((value) => value);
  const spec = ASSET_KINDS[block.type] || ASSET_KINDS.saras_pwo;
  const resolved = block.resolution_status === "resolved";
  const meta = metaFor(block, translate);
  const badge = Math.round(type.stepTitle.fontSize * 1.8);
  const asset = block.resolved_asset || {};
  // Core vs Extended is authored in the workspace; present mode has no card
  // grouping to carry it (one block per slide already), so the tier survives
  // as this small pill instead — see lessonPlans/LessonPlanWorkspace.js S2c.
  const tier = assetTierOf(block);
  const tierIsExtended = tier === ASSET_TIER_EXTENDED;

  // "What is this Play With Object (PWO)?" / "Summarize this video segment".
  //
  // Both paths end in the Saras panel on the right, which is where anything
  // that arrives mid-lesson belongs — it sits beside the slide rather than over
  // it, so the scene the summary is ABOUT stays on the wall while the room
  // reads about it.
  //
  // Drawn only where the host can actually carry it. The authored path needs a
  // host that can put text in the panel without asking; without one, sending
  // the label off to `onAskQuestion` would ask Saras the literal words "What is
  // this Play With Object (PWO)?" in front of a class — a slow, generic,
  // different-every-time answer to a question nobody asked. No control is the
  // better failure.
  const summary = assetSummaryFor(block);
  const summaryPress =
    summary?.kind === "authored" && onShowSummary
      ? () => onShowSummary(summary)
      : summary?.kind === "prompt" && onAskQuestion
        ? () => onAskQuestion(summary.prompt)
        : null;

  const [playing, setPlaying] = useState(false);
  const close = useCallback(() => setPlaying(false), []);

  // The quiz keeps going out to the caller: its player records answers, and
  // nothing in present mode may write one. Scenes and video open in place.
  const open = useCallback(() => {
    if (block.type === "saras_topic_quiz") {
      if (onOpenAsset) onOpenAsset(block);
      return;
    }
    setPlaying(true);
  }, [block, onOpenAsset]);

  // Once open the asset takes the whole slide. Nothing else competes with it,
  // because for the next ten minutes it is what the class is looking at.
  if (playing && resolved) {
    const Player = block.type === "saras_video" ? LessonVideoPlayer : LessonPwoPlayer;
    const playerProps =
      block.type === "saras_video"
        ? { sourceVideoId: asset.source_video_id, tagId: asset.tag_id, languageId }
        : { sourcePwoId: asset.source_pwo_id, sceneName: asset.scene_name, localLanguage };

    return (
      <View style={[styles.playingStage, { minHeight: type.assetMinHeight }]}>
        {/* The card is gone, and with it the asset's name and the summary
            control. Both are worth more once the thing is actually on the wall
            than they were before it: "what is this?" is what a room asks while
            it is looking at something. A thin bar keeps them without taking
            the picture's space. */}
        <View style={styles.playingBar}>
          <Text
            style={[styles.playingTitle, type.caption, { color: colors.textMuted }]}
            numberOfLines={1}
          >
            {titleFor(block, translate)}
          </Text>
          {summaryPress ? (
            <ChromeButton
              colors={colors}
              type={type}
              icon="robot-happy-outline"
              label={translate(summary.label)}
              onPress={summaryPress}
            />
          ) : null}
        </View>

        <View style={styles.playerFrame}>
          <Player
            {...playerProps}
            authToken={authToken}
            isDarkMode={isDarkMode}
            onClose={close}
            t={t}
          />
        </View>
      </View>
    );
  }

  return (
    <View
      style={[
        styles.stage,
        {
          minHeight: type.assetMinHeight,
          backgroundColor: colors.panelBg,
          borderColor: resolved ? `${spec.accent}55` : colors.incorrectBorder,
        },
      ]}
    >
      <View
        style={[
          styles.badge,
          {
            width: badge,
            height: badge,
            borderRadius: Math.round(badge * 0.28),
            backgroundColor: `${spec.accent}22`,
          },
        ]}
      >
        <MaterialCommunityIcons
          name={spec.icon}
          size={Math.round(badge * 0.55)}
          color={spec.accent}
        />
      </View>

      <View style={styles.kindRow}>
        <Text style={[styles.kind, type.eyebrow, { color: spec.accent }]}>
          {translate(spec.kind)}
        </Text>
        <View
          style={[
            styles.tierPill,
            {
              borderColor: tierIsExtended ? colors.border : colors.accentPrimary,
              backgroundColor: tierIsExtended ? colors.subtleBg : `${colors.accentPrimary}18`,
            },
          ]}
        >
          <Text
            style={[
              styles.tierPillText,
              type.caption,
              { color: tierIsExtended ? colors.textMuted : colors.accentPrimary },
            ]}
          >
            {translate(tierIsExtended ? "Extended" : "Core")}
          </Text>
        </View>
      </View>

      <Text style={[styles.title, type.stepTitle, { color: colors.textPrimary }]}>
        {titleFor(block, translate)}
      </Text>

      {meta.length > 0 ? (
        <Text style={[styles.meta, type.caption, { color: colors.textMuted }]}>
          {meta.join("  ·  ")}
        </Text>
      ) : null}

      {block.instructions ? (
        <PresentBody
          type={type}
          colors={colors}
          color={colors.textSecondary}
          style={styles.instructions}
        >
          {block.instructions}
        </PresentBody>
      ) : null}

      {resolved ? (
        // Without a caller wired up the stage still says what the scene is and
        // where it sits — a dead button would be worse than none.
        block.type !== "saras_topic_quiz" || onOpenAsset ? (
          <TouchableOpacity
            onPress={open}
            accessibilityRole="button"
            accessibilityLabel={translate(spec.action)}
            style={[
              styles.action,
              {
                backgroundColor: spec.accent,
                paddingVertical: Math.round(type.option.fontSize * 0.62),
                paddingHorizontal: Math.round(type.option.fontSize * 1.3),
              },
            ]}
          >
            <MaterialCommunityIcons
              name="play"
              size={Math.round(type.option.fontSize * 1.05)}
              color="#ffffff"
            />
            <Text style={[styles.actionText, type.option, { color: "#ffffff" }]}>
              {translate(spec.action)}
            </Text>
          </TouchableOpacity>
        ) : null
      ) : (
        // A broken reference must surface, never quietly become a blank stage —
        // the teacher needs to know before the room is watching.
        <View
          style={[
            styles.unresolved,
            {
              backgroundColor: colors.incorrectBg,
              borderColor: colors.incorrectBorder,
            },
          ]}
        >
          <MaterialCommunityIcons
            name="link-off"
            size={Math.round(type.caption.fontSize * 1.2)}
            color={colors.danger}
          />
          <Text
            style={[styles.unresolvedText, type.caption, { color: colors.textSecondary }]}
          >
            {translate("This resource could not be found in the current curriculum.")}
          </Text>
        </View>
      )}

      {/* Under the launch, and shown even where the reference is broken: the
          authored words are about the scene, not about the lookup that failed
          to find it. A teacher whose model has gone missing can still tell the
          room what they were about to look at. */}
      {summaryPress ? (
        <View style={styles.summaryRow}>
          <ChromeButton
            colors={colors}
            type={type}
            icon="robot-happy-outline"
            label={translate(summary.label)}
            onPress={summaryPress}
          />
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  // Full stage. The player draws its own frame and close control.
  stagePlayer: { width: "100%", flex: 1, borderRadius: 14, overflow: "hidden" },

  // The player keeps the stage; the bar above it is deliberately small enough
  // that it costs the picture almost nothing.
  playingStage: { width: "100%", flex: 1, minHeight: 0 },
  playingBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 8,
  },
  playingTitle: { flexShrink: 1 },
  playerFrame: { flex: 1, minHeight: 0, borderRadius: 14, overflow: "hidden" },
  stage: {
    width: "100%",
    borderRadius: 24,
    borderWidth: 2,
    paddingVertical: 44,
    paddingHorizontal: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  badge: { alignItems: "center", justifyContent: "center", marginBottom: 20 },
  kindRow: { flexDirection: "row", alignItems: "center", marginBottom: 10, gap: 10 },
  kind: {
    fontWeight: "800",
    letterSpacing: 1.4,
    textTransform: "uppercase",
  },
  tierPill: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 3,
  },
  tierPillText: { fontWeight: "800" },
  title: { fontWeight: "800", textAlign: "center" },
  meta: { marginTop: 12, textAlign: "center" },
  instructions: { marginTop: 20, textAlign: "center", maxWidth: 900 },
  action: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 999,
    marginTop: 30,
  },
  actionText: { fontWeight: "800", marginLeft: 12 },
  summaryRow: { marginTop: 18 },
  unresolved: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 14,
    borderWidth: 1,
    paddingHorizontal: 18,
    paddingVertical: 14,
    marginTop: 26,
    maxWidth: 720,
  },
  unresolvedText: { flex: 1, marginLeft: 12 },
});

export default PresentAssetBlock;
